import * as React from "react"
import { Box, Stack } from "@mui/material"
import useConfetti from "hooks/useConfetti"
import { Equation } from "@gillkyle/react-equation"

function LaunchConfetti() {
  const { fireConfetti } = useConfetti()

  React.useEffect(() => {
    fireConfetti({ small: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export const welcomeToLuzSteps = [
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <Box
          sx={{
            fontWeight: "bold",
          }}
        >{`Welcome to Luz!`}</Box>
        <Box>
          {`It's great to have you. You can skip out of this mini-tour by clicking the X button or outside this window at any time.`}
        </Box>
        <Box>{`Now, let's show you around.`}</Box>
      </Stack>
    ),
  },
  {
    selector: "[data-tour='welcome-page']",
    content: (
      <Stack spacing={1}>
        <Box>
          {`This is the main workspace area. Here, on the homepage, you can see a quick view of high level insights from across the platform.`}
        </Box>
      </Stack>
    ),
  },
  {
    selector: "[data-tour='sidebar']",
    content: (
      <Stack spacing={1}>
        <Box>
          {`This is the sidebar. It contains a list of all available products and links to the relevant pages.`}
        </Box>
      </Stack>
    ),
  },
  {
    selector: "[data-tour='quick-find-button']",
    content: (
      <Stack spacing={1}>
        <Box>
          {`If you're trying to look around the app quickly, you can take shortcuts to many pages and search for competitors you track with the Quick Find.`}
        </Box>
      </Stack>
    ),
  },
]

export const competitorOverviewSteps = [
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <Box
          sx={{
            fontWeight: "bold",
          }}
        >{`This is the Competitors Overview page`}</Box>
        <Box>
          {`It's a high level glance at your
          entire competitor portfolio.`}
        </Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="companies-overview__stat-card"]',
    content: (
      <Stack spacing={1}>
        <Box>
          At the top of the page, <b>Total Revenue</b> across all your tracked
          companies is listed.
        </Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="companies-overview__summary-card"]',
    content: (
      <Stack spacing={1}>
        <Box>
          Lower down the page is a table listing every company in your
          portfolio, showing revenue over time and their growth trend.
        </Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="companies-overview__search"]',
    content: (
      <Stack spacing={1}>
        <Box>
          You can search for a competitor by name, if you track many competitors
          at the same time.
        </Box>
      </Stack>
    ),
  },
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <LaunchConfetti />
        <Box
          sx={{
            fontWeight: "bold",
          }}
        >{`That's it!`}</Box>
        <Box>
          You can think of this page like a stock portfolio where quick stats
          are aggregated into an easily scannable list.
        </Box>
      </Stack>
    ),
  },
]

export const competitorProductsSteps = [
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <Box
          sx={{
            fontWeight: "bold",
          }}
        >{`This page shows the top products of your competitors`}</Box>
        <Box>
          {`The top selling products of all your tracked competitors are listed here.`}
        </Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="filters-toolbar"]',
    content: (
      <Stack spacing={1}>
        <Box>{`You can filter the products you see down in a variety of ways.`}</Box>
        <Box>
          {`For instance, you may want to look at only products of a given type for 2 companies.`}
        </Box>
      </Stack>
    ),
  },
  {
    selector: ".MuiDataGrid-row:nth-child(1)",
    content: (
      <Stack spacing={1}>
        <Box>{`If you want to dive deeper into a single product, you can view some quick stats or link directly to its page from here.`}</Box>
      </Stack>
    ),
  },
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <LaunchConfetti />
        <Box
          sx={{
            fontWeight: "bold",
          }}
        >{`That's it!`}</Box>
        <Box>
          {`This page can be very powerful if you want to find your competitor's
          biggest hits.`}
        </Box>
      </Stack>
    ),
  },
]

export const attributionOverviewSteps = [
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <Box
          sx={{
            fontWeight: "bold",
          }}
        >{`This is the Attribution Overview page`}</Box>
        <Box>
          {`It summarizes money you've made that we can attribute to different channels.`}
        </Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="attribution__selector"]',
    content: (
      <Stack spacing={1}>
        <Box>{`Attribution is calculated through different strategies that we label First, Last, or Multi touch.`}</Box>
        <Box>
          {`You can select which you'd like from this dropdown to compare performance attributing orders in different ways.`}
        </Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="attribution__stat-cards"]',
    content: (
      <Stack spacing={1}>
        <Box>{`We group up channels into several categories, orders attributed from Facebook ads are marked as Paid Ads, Klaviyo as Email/SMS, etc.`}</Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="attribution__orders-card"]',
    content: (
      <Stack spacing={1}>
        <Box>{`You can get an idea of each channel's performance in the bar chart and corresponding table.`}</Box>
        <Box>{`We calculate information like Site Visits and Conversion Rates for you here automatically.`}</Box>
      </Stack>
    ),
  },
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <LaunchConfetti />
        <Box
          sx={{
            fontWeight: "bold",
          }}
        >{`That's attribution!`}</Box>
        <Box>
          You can take an even deeper look at where your orders are coming from
          by exploring the other tabs within the attribution product.
        </Box>
      </Stack>
    ),
  },
]

export const findWebsiteConversionRateSteps = [
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <Box>{`The website analytics page has information about your store, including your overall conversion rate.`}</Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="site-analytics-card"]',
    content: (
      <Stack spacing={1}>
        <Box>{`This chart shows visitors and revenue, in order to find conversion rate we take the site visitors over the date range and divide it by the total number of orders from the same period.`}</Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Equation value="site visitors / (total orders)" />
        </Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="conversion-rate-card"]',
    content: (
      <Stack spacing={1}>
        <Box>{`That number is included on this stat card, including a trend comparison with the previous period.`}</Box>
      </Stack>
    ),
  },
]

export const findHighestValueProductsSteps = [
  {
    selector: "body",
    content: (
      <Stack spacing={1}>
        <Box>{`The website analytics Pages tab has information about each product/webpage.`}</Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="pages-scatterplot"]',
    content: (
      <Stack spacing={1}>
        <Box>{`This chart includes every product across your Shopify store. The line of best fit drawn through the points on the scatterplot shows the average revenue made per number of page views a product receives.`}</Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="pages-scatterplot"]',
    content: (
      <Stack spacing={1}>
        <Box>{`The highest value products are the ones that are furthest away above the line. They are the ones making the most revenue with the fewest page views.`}</Box>
        <Box>{`The lowest value products are the opposite, the points on the plot that are furthest away below the line.`}</Box>
      </Stack>
    ),
  },
  {
    selector: '[data-tour="pages-filter"]',
    content: (
      <Stack spacing={1}>
        <Box>{`To limit the points on the plot, you can filter the chart by product type here.`}</Box>
      </Stack>
    ),
  },
]
